import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Store } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PartnerShopCard } from "@/components/customer/PartnerShopCard";

/**
 * Public /partner-shops list page. Fetches every brand once and applies
 * All / Featured / <category> filters client-side plus a name search. Grid is
 * 2 cols on mobile, 3 at md, 4 at lg (mirrors the mobile app but on web).
 */
export default function CustomerPartnerShopsPage() {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("all"); // all | featured | <slug>
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/partner-shops?limit=200");
            setBrands(res.data?.items || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filters = useMemo(() => {
        const cats = [...new Set(brands.map((b) => b.category_slug).filter(Boolean))].sort();
        return [
            { key: "all", label: "All" },
            { key: "featured", label: "Featured" },
            ...cats.map((slug) => ({
                key: slug,
                label: slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            })),
        ];
    }, [brands]);

    const filtered = useMemo(() => {
        let out = brands;
        if (activeFilter === "featured") out = out.filter((b) => b.is_featured);
        else if (activeFilter !== "all") out = out.filter((b) => b.category_slug === activeFilter);
        const query = q.trim().toLowerCase();
        if (query) {
            out = out.filter(
                (b) =>
                    (b.brand_name || "").toLowerCase().includes(query) ||
                    (b.slug || "").toLowerCase().includes(query),
            );
        }
        return out;
    }, [brands, activeFilter, q]);

    return (
        <div className="min-h-screen bg-white pb-24 sm:pb-6" data-testid="customer-partner-shops-page">
            {/* Header */}
            <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-4 py-4 backdrop-blur-md md:px-8">
                <div className="mx-auto flex max-w-6xl items-center gap-3">
                    <Link
                        to="/home"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)] hover:opacity-80"
                        data-testid="partner-shops-back-btn"
                        aria-label="Back to home"
                    >
                        <ArrowLeft size={18} />
                    </Link>
                    <div className="min-w-0 flex-1">
                        <h1 className="font-heading text-xl font-extrabold text-[var(--text-primary)] md:text-2xl">
                            Kerala Partner Shops
                        </h1>
                        <p className="font-body text-xs text-[var(--text-muted)] md:text-sm">
                            Featured retail brands near you.
                        </p>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-4 pt-4 md:px-8">
                {/* Search */}
                <div className="relative mb-3">
                    <Search
                        size={16}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                    />
                    <Input
                        placeholder="Search brand name or slug…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="partner-shops-search"
                        className="pl-9 rounded-full border-slate-200 focus:border-[var(--brand-orange)] focus-visible:ring-1 focus-visible:ring-[var(--brand-orange)]"
                    />
                </div>

                {/* Filter chips */}
                <div
                    className="mb-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2"
                    data-testid="partner-shops-filters"
                >
                    {filters.map((f) => {
                        const active = activeFilter === f.key;
                        return (
                            <button
                                key={f.key}
                                onClick={() => setActiveFilter(f.key)}
                                data-testid={`partner-shops-filter-${f.key}`}
                                className={
                                    "shrink-0 snap-start rounded-full px-4 py-2 font-heading text-sm font-semibold transition " +
                                    (active
                                        ? "bg-[var(--brand-orange)] text-white shadow-sm"
                                        : "border border-slate-200 bg-white text-[var(--text-primary)] hover:bg-slate-50")
                                }
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="card-soft h-52 animate-pulse !bg-slate-100"
                                data-testid={`partner-shops-skeleton-${i}`}
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center">
                        <Store size={32} className="text-[var(--brand-orange)]" />
                        <div className="font-heading text-lg font-bold text-[var(--text-primary)]">
                            No brands match those filters
                        </div>
                        <div className="max-w-sm font-body text-sm text-[var(--text-muted)]">
                            Try clearing the search or picking a different category.
                        </div>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-4"
                        data-testid="partner-shops-grid"
                    >
                        {filtered.map((b) => (
                            <PartnerShopCard key={b.id} brand={b} source="partner_shops_list" />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
